import React, { useCallback, useContext, useEffect } from 'react'
import { object, string } from 'prop-types'
import { useRouter } from 'next/router'
import { http } from '@services'
import { Attachments } from '@dmi-mch/services'
import { useTracking } from '@hooks'
import { logger } from '@dmi-mch/utils'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import { PageContext } from '@providers/PageContext'
import { getSiteLanguage } from '@utils'

import ArtworkLayoutCatalog from './ArtworkLayoutCatalog'
import OnlineShowRoomArtwork from '@components/ot-modules/Catalog/Artworks/OSRoomArtworkV2'
import { ViewRoom } from '@components'
/*
Component that renders one of the pages:
- Art Basel Artwork
- OVR Artwork v2
- OVR Artwork in Room
*/
const ArtworkDetail = (props) => {
  const router = useRouter()
  const { labels: { ArtworkUploader = {} } = {} } = useLabelsFromAPI(['ArtworkUploader'], getSiteLanguage())
  const { notAvailableOptionLabel, costDisclaimer, reservedArtworkOptionLabel } = ArtworkUploader || {}
  const {
    page: { pageData: pageMetaInfo }
  } = useContext(PageContext)

  const { query } = router
  const viewInRoom = query && query.viewInRoom === 'true'
  const floorplan = query && query.floorplan === 'true'
  const roomInfo = query && query.roomInfo

  const { setAction } = useTracking(pageMetaInfo.id)
  const getPageLayout = useCallback(() => {
    let layout = null || ''
    if (pageMetaInfo && !pageMetaInfo.showInRoom) {
      layout = 'ab-artwork'
    }
    if (pageMetaInfo && pageMetaInfo.showInRoom && !viewInRoom && !floorplan) {
      layout = 'ovr-artwork'
    }
    if (pageMetaInfo && pageMetaInfo.showInRoom && viewInRoom) {
      layout = 'ovr-view-room'
    }
    if (pageMetaInfo && pageMetaInfo.showInRoom && floorplan) {
      layout = 'ovr-floorplan'
    }
    return layout
  }, [floorplan, pageMetaInfo, viewInRoom])

  // Used in Art Basel Artwork & OVR Artwork Detail only
  const attachments = new Attachments({ http })
  const [artworkAttachments, setArtworkAttachments] = React.useState(null)

  // Used to fetch Attachments for Art Basel Artwork & OVR Artwork Detail only
  React.useEffect(() => {
    const shouldFetchAttachments = () => {
      let should = false
      if (getPageLayout() === 'ab-artwork' || getPageLayout() === 'ovr-artwork-detail') {
        should = true
      }
      return should
    }
    const fetchAttachments = async () => {
      try {
        const attachmentsResponse = await attachments.get(2, pageMetaInfo.id, true)
        if (attachmentsResponse.ok) {
          setArtworkAttachments(attachmentsResponse.data)
        }
      } catch (e) {
        logger(e)
      }
    }
    pageMetaInfo?.id && shouldFetchAttachments() && fetchAttachments()
  }, [pageMetaInfo.id, getPageLayout]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    //call tracking api
    if (getPageLayout() === 'ovr-view-room') {
      setAction(5)
    }
    if (getPageLayout() === 'ovr-artwork') {
      setAction(2)
    }
  }, [pageMetaInfo.id, getPageLayout]) /* eslint-disable-line react-hooks/exhaustive-deps */

  if (!getPageLayout()) {
    return null
  }
  // Render Art Basel Artwork
  if (getPageLayout() === 'ab-artwork') {
    return (
      <ArtworkLayoutCatalog
        {...props}
        pageMetaInfo={pageMetaInfo}
        notAvailableOptionLabel={notAvailableOptionLabel}
        costDisclaimer={costDisclaimer}
        attachments={artworkAttachments}
        reservedArtworkOptionLabel={reservedArtworkOptionLabel}
      />
    )
  }

  // Render OVR Artwork View in Room
  if (getPageLayout() === 'ovr-view-room') {
    return <ViewRoom roomInfo={roomInfo} {...props} />
  }

  // Render OVR Artwork
  if (getPageLayout() === 'ovr-artwork') {
    return <OnlineShowRoomArtwork CMSProps={{ ...props, pageMetaInfo }} />
  }

  return null
}

ArtworkDetail.propTypes = {
  pageMetaInfo: object.isRequired,
  det_grp1_download_label: string,
  det_grp1_minute_label: string,
  det_grp1_label_size_cm: string,
  det_grp1_label_size_in: string,
  det_grp1_kilogram_label: string,
  det_grp1_cubic_metre_label: string,
  det_grp1_tab1_link: string,
  det_grp1_tab1_title: string,
  det_grp1_tab2_link: string,
  det_grp1_tab2_title: string,
  sharedLabels: object,
  router: object
}

export default ArtworkDetail
